import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Grid,
  TextField,
  MenuItem,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DatePicker, { DateObject } from "react-multi-date-picker";
import { API_URL } from "../Config/api";

const PackageServiceDetails = forwardRef(
  (
    { accessToken, onPackageDataChange, prefillPackageId, prefillSchedules },
    ref,
  ) => {
    const [packages, setPackages] = useState([]);
    const [selectedPackageId, setSelectedPackageId] = useState("");
    const [packageDetails, setPackageDetails] = useState(null);

    const [serviceSchedules, setServiceSchedules] = useState({});
    const [expandedPanel, setExpandedPanel] = useState(false);

    const [packageError, setPackageError] = useState(false);
    const [errors, setErrors] = useState({});

  
const getSrvKey = (srv, index) => {
 
  return srv.sub_service ? String(srv.sub_service) : `${srv.service_id}_${index}`;
};

    useImperativeHandle(ref, () => ({
      validate: () => {
        let newErrors = {};
        let firstErrorKey = null;
        let isPackageInvalid = !selectedPackageId;

        setPackageError(isPackageInvalid);

        if (packageDetails && packageDetails.Included_service) {
          packageDetails.Included_service.forEach((srv, index) => {
            const srvKey = getSrvKey(srv, index);
            const sched = serviceSchedules[srvKey];

            const hasValidDateCount =
              sched?.displayDates?.length === srv.duration;
            const hasTime = !!sched?.start_time;

            if (!hasValidDateCount || !hasTime) {
              newErrors[srvKey] = {
                date: !hasValidDateCount,
                time: !hasTime,
              };
              if (!firstErrorKey) firstErrorKey = srvKey;
            }
          });
        }

        setErrors(newErrors);

        if (isPackageInvalid) return false;
        if (firstErrorKey) {
          setExpandedPanel(firstErrorKey);
          return false;
        }

        return true;
      },
    }));

    const handleAccordionChange = (panel) => (event, isExpanded) => {
      setExpandedPanel((prev) => (prev === panel ? false : panel));
    };

    useEffect(() => {
      const fetchPackages = async () => {
        try {
          const res = await fetch(`${API_URL}/packages/active_packages/`, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          });
          const json = await res.json();
          if (json.data) {
            setPackages(json.data);
          }
        } catch (error) {
          console.error("Error fetching packages:", error);
        }
      };
      fetchPackages();
    }, [accessToken]);

    const handlePackageSelect = async (e) => {
      const pkgId = e.target.value;

      setSelectedPackageId(pkgId);
      setPackageError(false);
      setErrors({});
      setExpandedPanel(false);
      setServiceSchedules({});
      setPackageDetails(null);

      try {
        const res = await fetch(
          `${API_URL}/packages/package_details/?package_id=${pkgId}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          },
        );
        const json = await res.json();
        if (json.data && json.data.length > 0) {
          const details = json.data[0];
          setPackageDetails(details);

          const initialSchedules = {};
          details.Included_service.forEach((srv, index) => {
            const srvKey = getSrvKey(srv, index);
            initialSchedules[srvKey] = {
              start_time: "",
              end_time: "",
              dateRaw: [],
              date: [],
              displayDates: [],
            };
          });

          setServiceSchedules(initialSchedules);
          onPackageDataChange(pkgId, initialSchedules, details.package_cost);

          if (details.Included_service.length > 0) {
            setExpandedPanel(getSrvKey(details.Included_service[0], 0));
          }
        }
      } catch (error) {
        console.error("Error fetching package details:", error);
      }
    };

    const calculateEndTime = (start, hours) => {
      if (!start || !hours) return "";
      const [h, m] = start.split(":").map(Number);
      if (Number.isNaN(h) || Number.isNaN(m)) return "";
      const totalMinutes = h * 60 + m + hours * 60;
      const endH = Math.floor(totalMinutes / 60) % 24;
      const endM = Math.round(totalMinutes % 60);
      return `${String(endH).padStart(2, "0")}:${String(endM).padStart(2, "0")}`;
    };

    const handleStartTimeChange = (srvKey, value, serviceHours) => {
      const endTime = calculateEndTime(value, serviceHours);
      const updatedSchedules = {
        ...serviceSchedules,
        [srvKey]: {
          ...serviceSchedules[srvKey],
          start_time: value,
          end_time: endTime,
        },
      };
      setServiceSchedules(updatedSchedules);
      onPackageDataChange(
        selectedPackageId,
        updatedSchedules,
        packageDetails?.package_cost,
      );
    };

    const formatDateStr = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const buildPrefillSchedules = (details, rawSchedules) => {
      const schedules = {};
      details.Included_service.forEach((srv, index) => {
        const srvKey = getSrvKey(srv, index);
        // Fallback to service_id if sub_service_id is not in rawSchedules prefill
        const raw = rawSchedules?.[srvKey] || rawSchedules?.[srv.service_id];

        if (raw?.start_date && raw?.end_date) {
          const start = new Date(raw.start_date);
          const end = new Date(raw.end_date);
          const displayDates = [];
          for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            displayDates.push(formatDateStr(d));
          }
          schedules[srvKey] = {
            start_time: raw.start_time || "",
            end_time: raw.end_time || "",
            dateRaw: [[new DateObject(start), new DateObject(end)]],
            date: displayDates.map((d) => [d, d]),
            displayDates,
          };
        } else {
          schedules[srvKey] = {
            start_time: "",
            end_time: "",
            dateRaw: [],
            date: [],
            displayDates: [],
          };
        }
      });
      return schedules;
    };

    useEffect(() => {
      const loadPrefillPackage = async () => {
        if (!prefillPackageId) return;
        setSelectedPackageId(prefillPackageId);
        setPackageError(false);
        try {
          const res = await fetch(
            `${API_URL}/packages/package_details/?package_id=${prefillPackageId}`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
              },
            },
          );
          const json = await res.json();
          if (json.data && json.data.length > 0) {
            const details = json.data[0];
            setPackageDetails(details);
            const schedules = buildPrefillSchedules(details, prefillSchedules);
            setServiceSchedules(schedules);
            onPackageDataChange(
              prefillPackageId,
              schedules,
              details.package_cost,
            );
            if (details.Included_service.length > 0) {
              setExpandedPanel(getSrvKey(details.Included_service[0], 0));
            }
          }
        } catch (error) {
          console.error("Error prefilling package details:", error);
        }
      };
      loadPrefillPackage();
    }, [prefillPackageId, accessToken]);

    const handleDateChange = (srvKey, selectedDatesArray, maxDuration) => {
      const rawArray = Array.isArray(selectedDatesArray)
        ? selectedDatesArray
        : [selectedDatesArray];

      let validRawArray = [];
      let expandedDates = [];
      let currentDays = 0;

      for (let i = 0; i < rawArray.length; i++) {
        let item = rawArray[i];

        if (Array.isArray(item)) {
          if (item.length === 2) {
            let startDt = item[0]?.toDate
              ? item[0].toDate()
              : new Date(item[0]);
            let endDt = item[1]?.toDate ? item[1].toDate() : new Date(item[1]);
            startDt.setHours(0, 0, 0, 0);
            endDt.setHours(0, 0, 0, 0);

            if (startDt > endDt) {
              let t = startDt;
              startDt = endDt;
              endDt = t;
            }

            let diffDays =
              Math.round(Math.abs(endDt - startDt) / (1000 * 60 * 60 * 24)) + 1;

            if (currentDays + diffDays <= maxDuration) {
              validRawArray.push(item);
              for (
                let d = new Date(startDt);
                d <= endDt;
                d.setDate(d.getDate() + 1)
              ) {
                expandedDates.push(formatDateStr(d));
              }
              currentDays += diffDays;
            } else {
              let allowedDays = maxDuration - currentDays;
              if (allowedDays > 0) {
                let cappedEnd = new Date(startDt);
                cappedEnd.setDate(cappedEnd.getDate() + allowedDays - 1);

                validRawArray.push([new Date(startDt), new Date(cappedEnd)]);
                for (
                  let d = new Date(startDt);
                  d <= cappedEnd;
                  d.setDate(d.getDate() + 1)
                ) {
                  expandedDates.push(formatDateStr(d));
                }
                currentDays += allowedDays;
              }
              break;
            }
          } else if (item.length === 1) {
            if (currentDays + 1 <= maxDuration) {
              validRawArray.push(item);
              let d = item[0]?.toDate ? item[0].toDate() : new Date(item[0]);
              expandedDates.push(formatDateStr(d));
              currentDays += 1;
            } else break;
          }
        } else if (item) {
          if (currentDays + 1 <= maxDuration) {
            validRawArray.push(item);
            let d = item?.toDate ? item.toDate() : new Date(item);
            expandedDates.push(formatDateStr(d));
            currentDays += 1;
          } else break;
        }
      }

      const backendFormatted = expandedDates.map((dStr) => [dStr, dStr]);

      const updatedSchedules = {
        ...serviceSchedules,
        [srvKey]: {
          ...serviceSchedules[srvKey],
          dateRaw: validRawArray,
          date: backendFormatted,
          displayDates: expandedDates,
        },
      };

      setServiceSchedules(updatedSchedules);
      onPackageDataChange(
        selectedPackageId,
        updatedSchedules,
        packageDetails?.package_cost,
      );
    };

    const isServiceValid = (srvKey, duration) => {
      const sched = serviceSchedules[srvKey];
      return sched?.displayDates?.length === duration && !!sched?.start_time;
    };

    return (
      <Box sx={{ width: "100%", mt: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Select Package Plan"
              value={selectedPackageId}
              onChange={handlePackageSelect}
              error={packageError}
              helperText={
                packageError ? "Please select a package to continue" : ""
              }
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      overflowY: "auto",
                      mt: 0.5,
                      borderRadius: "12px",
                      "&::-webkit-scrollbar": { width: "6px" },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#bdbdbd",
                        borderRadius: "8px",
                      },
                    },
                  },
                },
              }}
            >
              {packages.map((pkg) => (
                <MenuItem key={pkg.package_id} value={pkg.package_id}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    width="100%"
                    alignItems="center"
                  >
                    <Typography variant="body1">{pkg.package_name}</Typography>
                    <Chip
                      label={`${pkg.duration} Days Overall`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ ml: 2, fontWeight: 500 }}
                    />
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        {packageDetails &&
          (() => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            return (
              <Box
                key={selectedPackageId}
                sx={{
                  mt: 3,
                  maxHeight: 450,
                  overflowY: "auto",
                  pr: 1,
                  pb: 2,

                  "&::-webkit-scrollbar": {
                    display: "none",
                  },

                  msOverflowStyle: "none",
                  scrollbarWidth: "none",
                }}
              >
                {packageDetails.Included_service.map((srv, index) => {
                  const srvKey = getSrvKey(srv, index);
                  const isValid = isServiceValid(srvKey, srv.duration);
                  const hasError = errors[srvKey];
                  const isExpanded = expandedPanel === srvKey;

                  const selectedCount =
                    serviceSchedules[srvKey]?.displayDates?.length || 0;

                  return (
                    <Accordion
                      key={srvKey}
                      expanded={isExpanded}
                      onChange={handleAccordionChange(srvKey)}
                      disableGutters
                      sx={{
                        mb: 1.5,
                        borderRadius: "12px !important",
                        border: isExpanded
                          ? "1px solid #1976d2"
                          : hasError
                            ? "1px solid #d32f2f"
                            : "1px solid #e0e0e0",
                        boxShadow: isExpanded
                          ? "0 4px 14px rgba(25, 118, 210, 0.12)"
                          : "0 1px 3px rgba(0,0,0,0.04)",
                        "&:before": { display: "none" },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <ExpandMoreIcon
                            color={isExpanded ? "primary" : "action"}
                          />
                        }
                        sx={{
                          backgroundColor: isExpanded ? "#f4f9ff" : "#ffffff",
                          borderRadius: "12px",
                          minHeight: "56px",
                        }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1.5}
                          width="100%"
                        >
                          {hasError ? (
                            <ErrorOutlineIcon color="error" fontSize="small" />
                          ) : isValid ? (
                            <CheckCircleOutlineIcon
                              color="success"
                              fontSize="small"
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: "#d3d3d3",
                                mx: 0.5,
                              }}
                            />
                          )}
                          <Box>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                color: hasError
                                  ? "#d32f2f"
                                  : isExpanded
                                    ? "#1976d2"
                                    : "#333",
                                fontWeight: 600,
                              }}
                            >
                              {srv.service_name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {srv.sub_service_name} • Required: {srv.duration}{" "}
                              Date(s)
                            </Typography>
                          </Box>
                        </Box>
                      </AccordionSummary>

                      <AccordionDetails
                        sx={{
                          pt: 2.5,
                          pb: 3,
                          px: 3,
                          backgroundColor: "#fafcff",
                          borderTop: "1px solid #edf2f7",
                          borderBottomLeftRadius: "12px",
                          borderBottomRightRadius: "12px",
                        }}
                      >
                        <Grid container spacing={2.5}>
                          <Grid item xs={12} md={12}>
                            <DatePicker
                              multiple
                              range
                              value={serviceSchedules[srvKey]?.dateRaw || []}
                              portal
                              fixMainPosition
                              minDate={today} 
                              
                              placeholder="Select Dates"
                              onChange={(dates) =>
                                handleDateChange(srvKey, dates, srv.duration)
                              }
                              containerStyle={{ width: "100%" }}
                              render={(value, openCalendar) => (
                                <TextField
                                  onClick={openCalendar}
                                  label={`Pick Exactly ${srv.duration} Date(s)`}
                                  size="small"
                                  fullWidth
                                  value={
                                    serviceSchedules[
                                      srvKey
                                    ]?.displayDates?.join(", ") || ""
                                  }
                                  InputProps={{
                                    readOnly: true,
                                    sx: {
                                      borderRadius: "8px",
                                      backgroundColor: "#fff",
                                    },
                                  }}
                                  error={!!errors[srvKey]?.date}
                                  helperText={
                                    errors[srvKey]?.date
                                      ? `Please select exactly ${srv.duration} date(s)`
                                      : `Selected: ${selectedCount} / ${srv.duration} Days`
                                  }
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={6} md={6}>
                            <TextField
                              type="time"
                              label="Start Time"
                              size="small"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              value={serviceSchedules[srvKey]?.start_time || ""}
                              error={!!errors[srvKey]?.time}
                              helperText={
                                errors[srvKey]?.time
                                  ? "Start time required"
                                  : ""
                              }
                              InputProps={{
                                sx: {
                                  borderRadius: "8px",
                                  backgroundColor: "#fff",
                                },
                              }}
                              onChange={(e) =>
                                handleStartTimeChange(
                                  srvKey,
                                  e.target.value,
                                  srv.Service_Time,
                                )
                              }
                            />
                          </Grid>
                          <Grid item xs={6} md={6}>
                            <TextField
                              type="time"
                              label="End Time (Auto)"
                              size="small"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              InputProps={{
                                readOnly: true,
                                sx: {
                                  borderRadius: "8px",
                                  backgroundColor: "#f5f5f5",
                                  color: "#757575",
                                },
                              }}
                              value={serviceSchedules[srvKey]?.end_time || ""}
                            />
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            );
          })()}
      </Box>
    );
  },
);

export default PackageServiceDetails;
